import { AfterViewChecked, ChangeDetectorRef, Component, Inject, OnDestroy, ViewChild } from "@angular/core";
import {
    NmsWizardStepModel,
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import { NmsInputButtonComponent } from "@nms-angular-toolkit/nms-input-button";
import { DeviceLinkModel, EndInputs, EndPortLink, LinkEnds, SelectedEnd } from "./link-device-model";
import {
    ANGULARJS_TRANSLATE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Device } from "@nms-ng2/app/modules/device/device-backup/device-backup.models";
import { EndTransmission, MuxDemux, Port, Slot, Transmission } from "../../../links.model";
import { MatSelect } from "@angular/material/select";
import { Subscription } from "rxjs";
import { LinkStepModelsUtilsService } from "@nms-ng2/app/modules/topology/links/services/link-step-models-utils";
import { DeviceModalOpenService } from "@nms-ng2/app/shared/services/devices/device-modal-open.service";
import { LinkDeviceEndTransmissionService } from "./services/link-device-end-transmission.service";
import { LinkService } from "../../../services/link.service";
import { LinkDevicePortIconsService } from "./services/link-device-port-icons.service";
import { LinkDeviceValidationService } from "./services/link-device-validation.service";
import { LinkDevicePortChannelMembersService } from "./services/link-device-port-channel-members.service";
import { LinkDeviceSlotPortService } from "./services/links-device-slot-port.service";
import { LinkDeviceMuxDemuxService } from "./services/link-device-mux-demux.service";

/**
 * Componente responsável por renderizar os dados da aba 'Equipamentos' na modal de adicionar Link.
 */
@Component({
    selector: "link-devices-step",
    templateUrl: "./link-devices-step.component.html",
    styleUrls: ["./link-devices-step.component.scss"]
})
export class LinkDevicesStepComponent extends AbstractNmsWizardStep implements AfterViewChecked, OnDestroy {
    readonly editDeviceIcon = "edit_square";

    model: DeviceLinkModel = new DeviceLinkModel();

    isAendMuxChecked: boolean = false;
    isZendMuxChecked: boolean = false;
    isAendModelDwdm: boolean = false;
    isZendModelDwdm: boolean = false;
    disabledASlotPort = { value: true };
    disabledZSlotPort = { value: true };
    invalidDevicesMessage = "";
    iconPortLinked: string = LinkDevicePortIconsService.ICON_PORT_LINKED;

    @ViewChild("aEndDeviceName")
    aEndDeviceName: NmsInputButtonComponent;
    @ViewChild("aEndModel")
    aEndModel: NmsInputButtonComponent;
    @ViewChild("zEndDeviceName")
    zEndDeviceName: NmsInputButtonComponent;
    @ViewChild("zEndModel")
    zEndModel: NmsInputButtonComponent;
    @ViewChild("aEndSlot")
    aEndSlot: MatSelect;
    @ViewChild("zEndSlot")
    zEndSlot: MatSelect;

    selectedAEnd = new SelectedEnd();
    selectedZEnd = new SelectedEnd();

    aEndInputs = new EndInputs();
    zEndInputs = new EndInputs();

    private aEndSlotChangesSubscription: Subscription;
    private zEndSlotChangesSubscription: Subscription;

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any, private readonly linkService: LinkService,
            private readonly linkStepModelsUtilsService: LinkStepModelsUtilsService, private readonly cdr: ChangeDetectorRef,
            private readonly linkDevicePortIconsService: LinkDevicePortIconsService,
            private readonly linkDeviceValidationService: LinkDeviceValidationService,
            private readonly linkDevicePortChannelMembersService: LinkDevicePortChannelMembersService,
            private readonly linkDeviceSlotPortService: LinkDeviceSlotPortService,
            private readonly linkDeviceMuxDemuxService: LinkDeviceMuxDemuxService,
            private readonly linkDeviceEndTransmissionService: LinkDeviceEndTransmissionService,
            private readonly deviceUtilsService: DeviceModalOpenService) {
        super();

        this.model.aEndLink = new EndPortLink();
        this.model.zEndLink = new EndPortLink();

        this.selectedAEnd.endTransmission = this.aEndInputs.endTransmissions
            .find(transmission => transmission.transmission === Transmission.TX);
        this.selectedZEnd.endTransmission = this.zEndInputs.endTransmissions
            .find(transmission => transmission.transmission === Transmission.RX);
    }

    ngAfterViewChecked() {
        this.aEndSlotChangesSubscription = this.aEndSlot?.valueChange.subscribe((slotValue: Slot) => {
            this.linkDeviceSlotPortService.handleSlotChanged(this.selectedAEnd, this.aEndInputs, Transmission.TX, slotValue);
        });
        this.zEndSlotChangesSubscription = this.zEndSlot?.valueChange.subscribe((slotValue: Slot) => {
            this.linkDeviceSlotPortService.handleSlotChanged(this.selectedZEnd, this.zEndInputs, Transmission.RX, slotValue);
        });
    }

    ngOnDestroy() {
        this.aEndSlotChangesSubscription?.unsubscribe();
        this.zEndSlotChangesSubscription?.unsubscribe();
    }

    async comingFromPreviousStep(deviceLinkModel: DeviceLinkModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        this.loadContent(wizardStepsCacheMap);

        return Promise.resolve(this.model);
    }

    async allowPrevious(): Promise<NmsWizardStepValidationResponse> {
        return Promise.resolve(new NmsWizardStepValidationResponse(true));
    }

    allowNext(): Promise<NmsWizardStepValidationResponse> {
        this.invalidDevicesMessage = this.linkDeviceValidationService
            .getEndsError(this.selectedAEnd.endDevice, this.selectedZEnd.endDevice);

        if (this.invalidDevicesMessage === "") {
            this.setLinkModel();

            const aEndLink = this.linkStepModelsUtilsService.buildEnd(this.model.aEndLink, this.model.isDwdm);
            const zEndLink = this.linkStepModelsUtilsService.buildEnd(this.model.zEndLink, this.model.isDwdm);
            const linkId = this.model.linkId;
            const ends = new LinkEnds(aEndLink, zEndLink, linkId);

            return this.linkService.validateEnds(ends);
        }

        return Promise.reject(new NmsWizardStepValidationResponse(false, this.translate.instant(this.invalidDevicesMessage)));
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        return Promise.resolve(this.model);
    }

    onClickAendDevice = () => {
        this.handleDataAfterDeviceSelection(
            this.aEndDeviceName,
            this.aEndModel,
            this.aEndInputs,
            this.selectedAEnd,
            this.disabledASlotPort
        );
    };

    onClickZendDevice = () => {
        this.handleDataAfterDeviceSelection(
            this.zEndDeviceName,
            this.zEndModel,
            this.zEndInputs,
            this.selectedZEnd,
            this.disabledZSlotPort
        );
    };

    handleDataAfterDeviceSelection(endDevice: NmsInputButtonComponent, endModel: NmsInputButtonComponent, endInputs: EndInputs,
                selectedEnd: SelectedEnd, disabledSlotPort: {value: boolean}) {
        const applyEquipment = (selectedDevice) => {
            selectedEnd.endDevice = selectedDevice;
            endDevice.model = selectedDevice.name;
            endModel.model = selectedDevice.model;
            endDevice.iconButtonChanged.emit(this.editDeviceIcon);
            disabledSlotPort.value = false;
            this.resetDataWhenChangeDevice(endDevice, selectedEnd);

            this.linkService.isDwdmSupported(endModel.model).subscribe(isDwdm => {
                const isAEnd = endDevice.fieldName === "aEndDevice";
                const isEndModelDwdmProperty  = isAEnd ? "isAendModelDwdm"  : "isZendModelDwdm";
                const isEndMuxCheckedProperty = isAEnd ? "isAendMuxChecked" : "isZendMuxChecked";

                this.loadDwdmContext(selectedEnd, endInputs, isEndModelDwdmProperty, isEndMuxCheckedProperty,
                    isDwdm, undefined, selectedDevice.id);
            });

            this.loadAllSlotsAndPorts(endInputs, selectedEnd);
        };

        const allowOnlyOneEquipment = true;
        this.deviceUtilsService.openDevicesModal([selectedEnd.endDevice], applyEquipment, allowOnlyOneEquipment);
    }

    getPortMembers(endDevice: Device, endPort: Port) {
        this.linkDevicePortChannelMembersService.getPortMembers(endDevice, endPort);
    }

    getPortMembersLinkTooltip(endPort: Port) {
        return this.linkDevicePortChannelMembersService.getPortMembersLinkTooltip(endPort);
    }

    public getLinkIconLabel(iconName): string {
        return this.linkDevicePortIconsService.getLinkIconLabel(iconName);
    }

    public getLinkIcons(): Array<string> {
        return this.linkDevicePortIconsService.getLinkIcons();
    }

    public getPortIcons(isDwdm: boolean, port: Port): Array<string> {
        return this.linkDevicePortIconsService.getPortIcons(isDwdm, port);
    }

    public isTransmissionLinked(isDwdm: boolean, isMuxSelected: boolean, mux: MuxDemux,
            port: Port, transmission: EndTransmission<Transmission>): boolean {
        return this.linkDeviceEndTransmissionService.isTransmissionLinked(isDwdm, isMuxSelected, mux, port, transmission);
    }

    private resetDataWhenChangeDevice(endDevice: NmsInputButtonComponent, selectedEnd: SelectedEnd) {
        const isEndMuxCheckedProperty = endDevice.fieldName === "aEndDevice" ? "isAendMuxChecked" : "isZendMuxChecked";
        this[isEndMuxCheckedProperty] = false;
        selectedEnd.endMuxSelected = null;
        selectedEnd.endSlotSelected = undefined;
        selectedEnd.endPortSelected = undefined;
    }

    private async loadContent(wizardStepsCacheMap: NmsWizardStepMapData): Promise<void> {
        this.model = wizardStepsCacheMap["DeviceLinkModel"] as DeviceLinkModel || this.model;
        this.model.aEndLink = this.model.aEndLink || new EndPortLink();

        this.disabledASlotPort.value = false;
        this.disabledZSlotPort.value = false;
        this.selectedAEnd.endDevice = this.model.aEndLink?.endDevice;

        const isAendDwdm = await this.linkService.isDwdmSupported(this.model.aEndLink.endDeviceModel).toPromise();
        this.loadDwdmContext(this.selectedAEnd, this.aEndInputs, "isAendModelDwdm", "isAendMuxChecked", isAendDwdm,
            this.model.aEndLink.endMuxResourceId, this.model.aEndLink.endDevice?.id);
        this.loadAllSlotsAndPorts(this.aEndInputs, this.selectedAEnd, this.model.aEndLink);
        if (this.selectedAEnd.endDevice) {
            this.aEndDeviceName.iconButtonChanged.emit(this.editDeviceIcon);
        }

        this.model.zEndLink = this.model.zEndLink || new EndPortLink();
        const isZendDwdm = await this.linkService.isDwdmSupported(this.model.zEndLink.endDeviceModel).toPromise();
        this.loadDwdmContext(this.selectedZEnd, this.zEndInputs, "isZendModelDwdm", "isZendMuxChecked", isZendDwdm,
            this.model.zEndLink.endMuxResourceId, this.model.zEndLink.endDevice?.id);
        this.selectedZEnd.endDevice = this.model.zEndLink?.endDevice;
        if (this.selectedZEnd.endDevice) {
            this.loadAllSlotsAndPorts(this.zEndInputs, this.selectedZEnd, this.model.zEndLink);
            this.zEndDeviceName.iconButtonChanged.emit(this.editDeviceIcon);
        }

        this.cdr.detectChanges();

        return Promise.resolve()
    }

    private loadAllSlotsAndPorts(endInputs: EndInputs, selectedEnd: SelectedEnd, endLink?: EndPortLink) {
        if (selectedEnd.endDevice?.id) {
            this.linkService.getSlotPorts(selectedEnd.endDevice.id).subscribe((slotItems: Slot[]) => {
                this.linkDeviceSlotPortService.initSlotPorts(slotItems, endInputs, selectedEnd, endLink);
                this.cdr.detectChanges();
            });
        }
    }

    private loadDwdmContext(selectedEnd: SelectedEnd, endInputs: EndInputs, isEndModelDwdm: string, isEndMuxChecked: string,
            isDwdm: boolean, muxResourceId: number, deviceResourceId: number) {
        this[isEndModelDwdm] = isDwdm;
        selectedEnd.endTransmission = undefined;

        if (isDwdm) {
            this[isEndMuxChecked] = !!muxResourceId;
            this.getMuxesForEnd(selectedEnd, endInputs, muxResourceId, deviceResourceId);
        }
    }

    private getMuxesForEnd(selectedEnd: SelectedEnd, endInputs: EndInputs, muxResourceId: number, deviceResourceId: number) {
        this.linkService.getMuxes(deviceResourceId).subscribe(muxes => {
            this.linkDeviceMuxDemuxService.initMuxes(muxes, endInputs, selectedEnd, muxResourceId);
        });
    }

    private setLinkModel() {
        this.model.isDwdm                      = this.isAendModelDwdm && this.isZendModelDwdm;

        this.model.aEndLink.endDeviceName      = this.aEndDeviceName.model;
        this.model.aEndLink.endResourceId      = this.selectedAEnd.endDevice.id;
        this.model.aEndLink.endDeviceModel     = this.aEndModel.model;
        this.model.aEndLink.endDeviceModelCode = this.selectedAEnd.endDevice.modelCode;
        this.model.aEndLink.endTransmission    = this.isAendModelDwdm
                                                    ? this.selectedAEnd.endTransmission?.transmission
                                                    : Transmission.NONE;
        this.model.aEndLink.muxDemux           = this.isAendMuxChecked;
        if (this.isAendMuxChecked) {
            this.model.aEndLink.endSlotId          = this.linkDeviceSlotPortService.INVALID_SLOT;
            this.model.aEndLink.endChassisNo       = this.linkDeviceSlotPortService.INVALID_CHASSIS;
            this.model.aEndLink.endPortNo          = this.linkDeviceSlotPortService.INVALID_PORT;
            this.model.aEndLink.endPortModelId     = this.linkDeviceSlotPortService.INVALID_PORT_MODEL_ID;
            this.model.aEndLink.endMuxResourceId   = this.selectedAEnd.endMuxSelected?.resourceId;
        } else {
            this.model.aEndLink.endSlotId          = this.selectedAEnd.endSlotSelected?.slotNo;
            this.model.aEndLink.endChassisNo       = this.selectedAEnd.endSlotSelected?.chassisNo;
            this.model.aEndLink.endPortNo          = this.selectedAEnd.endPortSelected?.portNo;
            this.model.aEndLink.endPortModelId     = this.selectedAEnd.endPortSelected?.portModelId;
            this.model.aEndLink.endMuxResourceId   = undefined;
        }

        this.model.zEndLink.endDeviceName      = this.zEndDeviceName.model;
        this.model.zEndLink.endResourceId      = this.selectedZEnd.endDevice.id;
        this.model.zEndLink.endDeviceModel     = this.zEndModel.model;
        this.model.zEndLink.endDeviceModelCode = this.selectedZEnd.endDevice.modelCode;
        this.model.zEndLink.endTransmission    = this.isZendModelDwdm
                                                    ? this.selectedZEnd.endTransmission?.transmission
                                                    : Transmission.NONE;
        this.model.zEndLink.muxDemux           = this.isZendMuxChecked;
        if (this.isZendMuxChecked) {
            this.model.zEndLink.endSlotId          = this.linkDeviceSlotPortService.INVALID_SLOT;
            this.model.zEndLink.endChassisNo       = this.linkDeviceSlotPortService.INVALID_CHASSIS;
            this.model.zEndLink.endPortNo          = this.linkDeviceSlotPortService.INVALID_PORT;
            this.model.zEndLink.endPortModelId     = this.linkDeviceSlotPortService.INVALID_PORT_MODEL_ID;
            this.model.zEndLink.endMuxResourceId   = this.selectedZEnd.endMuxSelected?.resourceId;
        } else {
            this.model.zEndLink.endSlotId          = this.selectedZEnd.endSlotSelected?.slotNo;
            this.model.zEndLink.endChassisNo       = this.selectedZEnd.endSlotSelected?.chassisNo;
            this.model.zEndLink.endPortNo          = this.selectedZEnd.endPortSelected?.portNo;
            this.model.zEndLink.endPortModelId     = this.selectedZEnd.endPortSelected?.portModelId;
            this.model.zEndLink.endMuxResourceId   = undefined;
        }
    }

}
